
import React, { useEffect, useState, useRef, lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Hero from '../components/Hero';
import ParticleBackground from '../components/ParticleBackground';
import ThemeLanguageSwitch from '../components/ThemeLanguageSwitch';
import ScrollProgress from '../components/ScrollProgress';
import ScrollToTop from '../components/ScrollToTop';
import PageTransition from '../components/PageTransition';
import { useSettingsStore } from '../store/useSettingsStore';
import { useIsMobile } from '../hooks/use-mobile';
import { Skeleton } from '@/components/ui/skeleton';
import { AboutSkeleton, GenericSkeleton, ProjectsSkeleton, ServicesSkeleton } from '@/components/skeletons/SectionSkeletons';

// Lazy loading only for non-critical components that are below the fold
const About = lazy(() => import('../components/About'));
const Projects = lazy(() => import('../components/Projects'));
const Experience = lazy(() => import('../components/Experience'));
const Contact = lazy(() => import('../components/Contact'));
const Footer = lazy(() => import('../components/Footer'));
const Testimonials = lazy(() => import('../components/Testimonials'));
const Services = lazy(() => import('../components/Services'));
const Certifications = lazy(() => import('../components/Certifications'));
const NeonTransition = lazy(() => import('../components/NeonTransition'));

const Index = () => {
  const sectionsRef = useRef<HTMLElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const theme = useSettingsStore(state => state.theme);
  const isMobile = useIsMobile();
  
  // Optimized particle count based on device capabilities
  const getParticleCount = () => {
    if (isMobile) return navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4 ? 8 : 12;
    return navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4 ? 20 : 25;
  };

  // Simplified useEffect without Promise.all to reduce overhead
  useEffect(() => {
    // Skip preloading for faster initial render
    const timeout = setTimeout(() => {
      if (isLoading) {
        setIsLoading(false);
      }
    }, 1000); // Reduced timeout
    
    return () => clearTimeout(timeout);
  }, []);

  return (
    <PageTransition>
      <motion.div 
        className="min-h-screen relative"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ 
          duration: isMobile ? 0.2 : 0.4 
        }}
      >
        <ParticleBackground particleCount={getParticleCount()} />
        
        <ScrollProgress />
        <ScrollToTop />
        <ThemeLanguageSwitch />
        <Navbar />
        
        <main ref={sectionsRef}>
          <Hero />
          
          <Suspense fallback={<GenericSkeleton />}>
            <Experience />
          </Suspense>
          <Suspense fallback={null}>
            <NeonTransition />
          </Suspense>
          <Suspense fallback={<AboutSkeleton />}>
            <About />
          </Suspense>
          <Suspense fallback={null}>
            <NeonTransition />
          </Suspense>
          <Suspense fallback={<ProjectsSkeleton />}>
            <Projects />
          </Suspense>
          <Suspense fallback={null}>
            <NeonTransition />
          </Suspense>
          <Suspense fallback={<GenericSkeleton />}>
            <Certifications />
          </Suspense>
          <Suspense fallback={null}>
            <NeonTransition />
          </Suspense>
          <Suspense fallback={<GenericSkeleton />}>
            <Testimonials />
          </Suspense>
          <Suspense fallback={null}>
            <NeonTransition />
          </Suspense>
          <Suspense fallback={<ServicesSkeleton />}>
            <Services />
          </Suspense>
          <Suspense fallback={null}>
            <NeonTransition />
          </Suspense>
          <Suspense fallback={<GenericSkeleton />}>
            <Contact />
          </Suspense>
        </main>
        
        <Suspense fallback={<Skeleton className="h-24 w-full" />}>
          <Footer />
        </Suspense>
      </motion.div>
    </PageTransition>
  );
};

export default Index;
